import { useGetEvents } from "@/api/admin/useGetEvents";
import { useGetReservationRequests } from "@/api/admin/useGetReservationRequests";
import { useUpdateEvent } from "@/api/admin/useUpdateEvent";
import { ReservationRequestDto } from "@/api/generated/generatedTypes";
import { notifications } from "@mantine/notifications";
import { useMemo, useState } from "react";
import type { ExistingEventWithStaging } from "../ExistingEventCard/types";
import { EventSortOption } from "../ExistingEventsSection/EventSort";
import { showErrorNotification } from "../utils/errorNotifications";
import { UsePendingRequestsReturn } from "./usePendingRequests";

const EXISTING_EVENTS_STAGING_KEY = "existing-events-staging";

interface ExistingEventStagingData {
  [eventId: string]: {
    stagedRequestIds: number[];
  };
}

const saveExistingEventsStagingToStorage = (data: ExistingEventStagingData) => {
  try {
    localStorage.setItem(EXISTING_EVENTS_STAGING_KEY, JSON.stringify(data));
  } catch (error) {
    console.error("Failed to save existing events staging to localStorage:", error);
  }
};

export interface UseExistingEventsParams {
  eventsResponse: ReturnType<typeof useGetEvents>["data"];
  requestsResponse: ReturnType<typeof useGetReservationRequests>["data"];
  pendingRequestsHook: UsePendingRequestsReturn;
  updateEventMutation: ReturnType<typeof useUpdateEvent>;
  onEventsUpdated: () => void;
  onSelectExistingEvent: (eventId: number | null) => void;
}

export interface UseExistingEventsReturn {
  eventsWithStaging: ExistingEventWithStaging[];
  sortedEvents: ExistingEventWithStaging[];
  selectedEvent: ExistingEventWithStaging | null;
  sortOption: EventSortOption;
  handleRemoveStaged: (eventId: number, requestId: number) => void;
  handleAddRequestToEvent: (eventId: number, request: ReservationRequestDto) => void;
  handleSave: (eventId: number) => void;
  handleSaveVenue: (
    eventId: number,
    restaurantId: number | null,
    afterPartyId: number | null
  ) => Promise<void>;
  handleConfirm: (eventId: number) => Promise<void>;
  handleSelect: (event: ExistingEventWithStaging | null) => void;
  setSortOption: (option: EventSortOption) => void;
}

/**
 * Hook to manage existing events staging, sorting, and operations
 * Handles derived state computation, localStorage persistence, and event updates
 */
export function useExistingEvents({
  eventsResponse,
  requestsResponse,
  pendingRequestsHook,
  updateEventMutation,
  onEventsUpdated,
  onSelectExistingEvent,
}: UseExistingEventsParams): UseExistingEventsReturn {
  const [selectedExistingEventId, setSelectedExistingEventId] = useState<number | null>(null);
  const [existingEventsStagingOverrides, setExistingEventsStagingOverrides] =
    useState<ExistingEventStagingData>({});
  const [existingEventsSortOption, setExistingEventsSortOption] =
    useState<EventSortOption>("startTime-asc");

  // Compute eventsWithStaging from backend events + localStorage
  const eventsWithStaging = useMemo(() => {
    const savedData = localStorage.getItem(EXISTING_EVENTS_STAGING_KEY);
    const stagingData: ExistingEventStagingData = savedData ? JSON.parse(savedData) : {};
    const mergedStagingData = { ...stagingData, ...existingEventsStagingOverrides };

    const allRequests = requestsResponse?.reservationRequests || [];
    const result = (eventsResponse?.events || []).map((event) => {
      const savedRequestIds = mergedStagingData[event.id]?.stagedRequestIds || [];
      const allAvailableRequests = [...allRequests, ...event.reservationRequests];
      const stagedRequests = savedRequestIds
        .map((id) => allAvailableRequests.find((req) => req.id === id))
        .filter((req): req is ReservationRequestDto => req !== undefined);

      return {
        ...event,
        stagedRequests,
      };
    });

    return result;
  }, [eventsResponse, requestsResponse, existingEventsStagingOverrides]);

  // Derive selectedExistingEvent from eventsWithStaging + selectedExistingEventId (single source of truth)
  const selectedExistingEvent = useMemo(() => {
    if (!selectedExistingEventId) return null;
    return eventsWithStaging.find((e) => e.id === selectedExistingEventId) || null;
  }, [eventsWithStaging, selectedExistingEventId]);

  // Sort existing events
  const sortedEventsWithStaging = useMemo(() => {
    const sorted = [...eventsWithStaging];
    // Many events share a start time, so id breaks ties to keep the order stable after refetches.
    switch (existingEventsSortOption) {
      case "startTime-asc":
        return sorted.sort(
          (a, b) => new Date(a.startTime).getTime() - new Date(b.startTime).getTime() || a.id - b.id
        );
      case "startTime-desc":
        return sorted.sort(
          (a, b) => new Date(b.startTime).getTime() - new Date(a.startTime).getTime() || b.id - a.id
        );
      case "createdAt-asc":
        return sorted.sort(
          (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime() || a.id - b.id
        );
      case "createdAt-desc":
        return sorted.sort(
          (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime() || b.id - a.id
        );
      default:
        return sorted;
    }
  }, [eventsWithStaging, existingEventsSortOption]);

  const handleRemoveStaged = (eventId: number, requestId: number) => {
    const event = eventsWithStaging.find((e) => e.id === eventId);
    if (!event) return;

    const removedRequest = event.stagedRequests.find((r) => r.id === requestId);
    const updatedRequestIds = event.stagedRequests
      .filter((r) => r.id !== requestId)
      .map((r) => r.id);

    setExistingEventsStagingOverrides((prev) => {
      const updated = {
        ...prev,
        [eventId]: { stagedRequestIds: updatedRequestIds },
      };
      setTimeout(() => saveExistingEventsStagingToStorage(updated), 0);
      return updated;
    });

    if (removedRequest) {
      pendingRequestsHook.addRequest(removedRequest);
    }
  };

  const handleAddRequestToEvent = (eventId: number, request: ReservationRequestDto) => {
    const event = eventsWithStaging.find((e) => e.id === eventId);
    if (!event) return;

    const updatedRequestIds = [...event.stagedRequests.map((r) => r.id), request.id];

    setExistingEventsStagingOverrides((prev) => ({
      ...prev,
      [eventId]: { stagedRequestIds: updatedRequestIds },
    }));

    pendingRequestsHook.removeRequest(request.id);
  };

  const handleSave = (eventId: number) => {
    const stagingData = { ...existingEventsStagingOverrides };
    eventsWithStaging.forEach((event) => {
      if (event.stagedRequests.length > 0) {
        stagingData[event.id] = {
          stagedRequestIds: event.stagedRequests.map((r) => r.id),
        };
      }
    });
    saveExistingEventsStagingToStorage(stagingData);
    notifications.show({
      title: "저장 완료",
      message: "그룹이 저장되었습니다",
      color: "green",
    });
  };

  const handleSaveVenue = async (
    eventId: number,
    restaurantId: number | null,
    afterPartyId: number | null
  ) => {
    try {
      await updateEventMutation.mutateAsync({
        eventId,
        restaurantId,
        afterPartyVenueId: afterPartyId,
      });

      notifications.show({
        title: "베뉴 저장 완료",
        message: "베뉴 정보가 성공적으로 저장되었습니다",
        color: "green",
      });
    } catch (error) {
      console.error("Failed to save venue:", error);
      showErrorNotification(error, "베뉴 저장 실패");
    }
  };

  const handleConfirm = async (eventId: number) => {
    const event = eventsWithStaging.find((e) => e.id === eventId);
    if (!event || event.stagedRequests.length === 0) return;

    try {
      await updateEventMutation.mutateAsync({
        eventId,
        reservationRequestIds: event.stagedRequests.map((r) => r.id),
      });

      setExistingEventsStagingOverrides((prev) => {
        const updated = {
          ...prev,
          [eventId]: { stagedRequestIds: [] },
        };
        setTimeout(() => saveExistingEventsStagingToStorage(updated), 0);
        return updated;
      });

      notifications.show({
        title: "이벤트 확정",
        message: "이벤트가 성공적으로 확정되었습니다",
        color: "green",
      });

      onEventsUpdated();
    } catch (error) {
      console.error("Failed to confirm event:", error);
      showErrorNotification(error, "이벤트 확정 실패");
    }
  };

  const handleSelect = (event: ExistingEventWithStaging | null) => {
    const eventId = event?.id || null;
    setSelectedExistingEventId(eventId);
    onSelectExistingEvent(eventId);
  };

  return {
    eventsWithStaging,
    sortedEvents: sortedEventsWithStaging,
    selectedEvent: selectedExistingEvent,
    sortOption: existingEventsSortOption,
    handleRemoveStaged,
    handleAddRequestToEvent,
    handleSave,
    handleSaveVenue,
    handleConfirm,
    handleSelect,
    setSortOption: setExistingEventsSortOption,
  };
}
